// =============================================================================
// ui/state/useCustomWidgets.ts — the only place a widget is written.
// -----------------------------------------------------------------------------
// `domain/customWidgets` decides what the flags blob should become. This decides
// whether it is safe to save one at all, and reports what happened. Same shape
// as `useLeakRules`, for the same reason and against the same column.
//
// ── 🚨 THE GATE ──
//
// `usePreferences` keeps `update()` callable while a read has FAILED, because a
// patch normally carries only the field being changed and cannot revert what it
// never saw. `app_flags` is the exception: the patch IS the whole blob. After a
// failed read `prefs` is null, the blob spread would be `{}`, and saving a
// widget would wipe the leak rules, every layout choice and the P&L unit.
//
// So `canWrite` is checked before every write, and a refusal says so in words.
//
// ── 🚨 THE COLLECTION HAZARD, WHICH IS THIS FILE'S OWN (D141) ──
//
// The value is an ARRAY. Every mutation re-reads `prefs.appFlags` at CALL time
// rather than closing over a snapshot — two widgets added without a reload
// between them would otherwise both spread the same starting array, and the
// second would erase the first.
//
// ⚠ AND THE ARRAY IS REBUILT FROM WHAT IS ON DISK, not from the rendered list.
// `readWidgets` normalises, and a widget stored in the pre-overlay shape comes
// back normalised — which is what we want to write. But the caller edits ONE
// widget, so the mutation is expressed as "replace index i in the array as it is
// NOW", never as "save the array this component last rendered".
//
// ── ⚠ THE OPTIMISTIC UPDATE IS NOT ROLLED BACK ──
//
// `usePreferences.update()` applies the patch locally first and leaves it
// applied when the save fails, so a control does not jump under the cursor. That
// is right for a text field and misleading here: a widget would sit on screen
// looking saved. So the error is surfaced and the screen says it.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { usePreferences } from './preferences';
import {
  newWidget, readWidgets, withWidgets, type CustomWidget,
} from '../../selectors/customAnalytics';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface CustomWidgetsState {
  widgets: CustomWidget[];
  /** False while preferences are loading or unreadable — no write may proceed. */
  canWrite: boolean;
  busy: boolean;
  error: string | null;
  /** Replace one widget. Returns the failure message, or null on success. */
  replace: (index: number, next: CustomWidget) => Promise<string | null>;
  remove: (index: number) => Promise<string | null>;
  add: () => Promise<string | null>;
}

const NOT_READY = 'Your settings haven’t loaded, so nothing can be saved yet — '
  + 'saving now would overwrite the ones this screen never read. Reload and try again.';

export function useCustomWidgets(): CustomWidgetsState {
  const { phase, prefs, update, saveError } = usePreferences();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = phase === 'ready' && prefs !== null;
  const widgets = useMemo(() => readWidgets(prefs?.appFlags), [prefs]);

  /**
   * ⚠ `edit` receives the array AS IT IS ON DISK RIGHT NOW, read inside the
   * callback. See the header — this is the whole defence against a stale
   * snapshot dropping a widget.
   */
  const write = useCallback(
    async (edit: (current: CustomWidget[]) => CustomWidget[]): Promise<string | null> => {
      if (!canWrite || !prefs) { setError(NOT_READY); return NOT_READY; }
      const next = edit(readWidgets(prefs.appFlags));
      setBusy(true);
      setError(null);
      try {
        await update({ appFlags: withWidgets(prefs.appFlags, next) });
        return null;
      } catch (e) {
        const msg = reportFailure('save custom widget', e, 'The widget could not be saved.');
        setError(msg);
        return msg;
      } finally {
        setBusy(false);
      }
    },
    // 🚨 `prefs` IS A DEPENDENCY ON PURPOSE. Dropping it leaves this callback
    // holding the blob as it was at first render, and the negative control that
    // proved it fails exactly three tests: two adds, add-then-edit, and
    // add-then-remove all lose the earlier edit — while the screen still shows
    // it, because the optimistic patch left it there.
    [canWrite, prefs, update],
  );

  const replace = useCallback(
    (index: number, next: CustomWidget) => write(
      (cur) => cur.map((w, i) => (i === index ? next : w)),
    ),
    [write],
  );

  const remove = useCallback(
    (index: number) => write((cur) => cur.filter((_, i) => i !== index)),
    [write],
  );

  const add = useCallback(() => write((cur) => [...cur, newWidget()]), [write]);

  return {
    widgets, canWrite, busy,
    // ⚠ Either failure counts. A save that was refused here and a save the
    // network rejected are the same thing to the trader: it did not stick.
    error: error ?? saveError,
    replace, remove, add,
  };
}
